import { config } from '../../config';
import { getToken } from '../lib/localStorageHandler';

export interface ActionButton {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'danger' | 'success';
  action: ActionType;
  data?: any;
  disabled?: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
  toolCalls?: ToolCall[];
  actionButtons?: ActionButton[];
  escalated?: boolean;
  feedback?: {
    type: 'like' | 'dislike';
    timestamp: Date;
    comment?: string;
  };
  id?: string; // Unique identifier for the message
}

export interface ToolCall {
  id: string;
  name: string;
  args: any;
  result?: any;
}

export interface ChatContext {
  userName?: string;
  userEmail?: string;
  userId?: string;
  subscription?: {
    package?: string;
    term?: string;
    price?: number;
    renewalDate?: string;
    currentPeriodStartedAt?: string;
    trialEndDate?: string;
    status?: string;
  };
  usage?: {
    uploadCount?: number;
    uploadLimit?: number | string;
    uploadRemaining?: number | string;
    askAICount?: number;
    askAILimit?: number | string;
    askAIRemaining?: number | string;
    transcriptionCount?: number;
    transcriptionLimit?: number | string;
    transcriptionRemaining?: number | string;
    videoAskAICount?: number;
  };
  team?: {
    name?: string;
    membersCount?: number;
    teamId?: string;
    ownerEmail?: string;
    role?: string;
  };
  urlContext?: {
    currentPage?: string;
    path?: string;
    fileId?: string;
    teamId?: string;
    section?: string;
    hostname?: string;
    protocol?: string;
    port?: string;
    referrer?: string;
    title?: string;
    hash?: string;
    search?: string;
    urlParams?: Record<string, string>;
  };
  browserInfo?: {
    name?: string;
    version?: string;
    fullUserAgent?: string;
    language?: string;
    languages?: string;
    platform?: string;
    vendor?: string;
    cookieEnabled?: boolean;
    onLine?: boolean;
  };
  deviceInfo?: {
    type?: string;
    maxTouchPoints?: number;
    screenWidth?: number;
    screenHeight?: number;
    viewportWidth?: number;
    viewportHeight?: number;
    pixelRatio?: number;
  };
  localStorage?: Record<string, any>;
  sessionInfo?: {
    sessionStart?: string;
    tabId?: string;
    timeZone?: string;
    timestamp?: string;
    referrer?: string;
    origin?: string;
  };
}

export interface ChatResponse {
  success: boolean;
  data?: {
    message: string;
    sessionId?: string;
    sessionTitle?: string;
    shouldEscalate: boolean;
    escalated?: boolean;
    toolCalls?: ToolCall[];
    selectedAgent?: string;
    routingConfidence?: number;
  };
  message?: string;
  errors?: any[];
}

export interface ChatSession {
  sessionId: string;
  title?: string;
  lastMessageAt: Date;
  status: 'active' | 'closed' | 'escalated';
  createdAt: Date;
}

export interface ChatHistoryResponse {
  success: boolean;
  data?: {
    sessions: ChatSession[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
  message?: string;
}

export interface ChatSessionResponse {
  success: boolean;
  data?: {
    sessionId: string;
    title?: string;
    messages: ChatMessage[];
    context?: ChatContext;
    status: 'active' | 'closed' | 'escalated';
    lastMessageAt: Date;
    createdAt: Date;
    updatedAt: Date;
  };
  message?: string;
}

// Action types that can be triggered by buttons
export type ActionType =
  | 'reprocess_transcription'
  | 'reprocess_started'
  | 'go_to_billing'
  | 'clear_cache_and_storage'
  | 'cancellation_help'
  | 'generate_specific_navigation_button'
  | 'error';

export interface ReprocessTranscriptionAction {
  name: 'reprocessTranscription';
  args: {
    fileId: string;
    language?: string;
  };
  result: {
    success: boolean;
    message: string;
    action: ActionType;
    fileId?: string;
    fileName?: string;
  };
}

export interface CheckTranscriptionStatusAction {
  name: 'checkTranscriptionStatus';
  args: {
    fileId: string;
  };
  result: {
    success: boolean;
    message: string;
    status?: 'completed' | 'processing' | 'not_started' | 'not_applicable';
    action: ActionType;
    fileId?: string;
    fileName?: string;
  };
}

export interface GetFileInfoAction {
  name: 'getFileInfo';
  args: {
    fileId?: string;
    fileName?: string;
  };
  result: {
    success: boolean;
    message?: string;
    fileInfo?: {
      id: string;
      name: string;
      duration?: number;
      hasAudio: boolean;
      hasTranscript: boolean;
      transcriptInProgress: boolean;
      transcriptRequestedAt?: string;
      transcriptCompletedAt?: string;
      streams?: string[];
    };
    action: ActionType;
  };
}

export type ChatAction =
  | ReprocessTranscriptionAction
  | CheckTranscriptionStatusAction
  | GetFileInfoAction;

// Helper function to trigger actual reprocessing via the API
export const triggerReprocessTranscription = async (
  fileId: string,
  language?: string
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${config.authBaseUrlV2}/files/actions/${fileId}/reprocess`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          language,
        }),
      }
    );

    if (!response.ok) {
      console.error(
        'Failed to trigger reprocessing:',
        response.status,
        response.statusText
      );
      return false;
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Error triggering reprocessing:', error);
    return false;
  }
};

export const sendChatMessage = async (
  message: string,
  context?: ChatContext,
  conversationHistory?: ChatMessage[],
  sessionId?: string
): Promise<{ message: string; sessionId?: string }> => {
  try {
    console.log('Making chat request to:', `${config.authBaseUrlV2}/chat`);
    console.log('Request payload:', { message, context, conversationHistory });
    console.log('Token:', getToken() ? 'Present' : 'Missing');

    const response = await fetch(`${config.authBaseUrlV2}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        message,
        context,
        conversationHistory,
        sessionId,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('Error response:', errorText);
      throw new Error(
        `Failed to send chat message: ${response.status} ${response.statusText}`
      );
    }

    const data: ChatResponse = await response.json();
    console.log('Response data:', data);

    if (!data.success) {
      throw new Error(data.message || 'Failed to send chat message');
    }

    return {
      message: data.data?.message || 'Sorry, I could not process your request.',
      sessionId: data.data?.sessionId,
    };
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw new Error('Failed to send chat message');
  }
};

// Function to parse action buttons from AI response
export const parseActionButtons = (responseText: string): ActionButton[] => {
  const buttons: ActionButton[] = [];

  // Look for action button patterns in the response
  const buttonPatterns: Array<{
    pattern: RegExp;
    action: ActionType;
    type: 'primary' | 'secondary' | 'danger' | 'success';
    label?: string;
  }> = [
    {
      // Enhanced pattern: [BUTTON:REPROCESS_TRANSCRIPTION:Label|fileId:FILE_ID] or [BUTTON:REPROCESS_TRANSCRIPTION:Label]
      pattern: /\[BUTTON:REPROCESS_TRANSCRIPTION:([^|\]]+)(?:\|fileId:([^|\]]+))?\]/g,
      action: 'reprocess_transcription' as ActionType,
      type: 'primary' as const,
    },
    {
      pattern: /\[BUTTON:GO_TO_BILLING:([^\]]+)\]/g,
      action: 'go_to_billing' as ActionType,
      type: 'primary' as const,
    },
    {
      // Fix: Handle both CLEAR_CACHE_AND_STORAGE and clearCacheAndStorage formats
      pattern: /\[BUTTON:(?:CLEAR_CACHE_AND_STORAGE|clearCacheAndStorage):([^|\]]+)(?:\|[^|\]]*)*\]/g,
      action: 'clear_cache_and_storage' as ActionType,
      type: 'danger' as const,
    },
    {
      // Handle both BUTTON and TOKEN formats for CANCELLATION_HELP
      pattern: /\[BUTTON:CANCELLATION_HELP:([^\]]+)\]/g,
      action: 'cancellation_help' as ActionType,
      type: 'primary' as const,
    },
    {
      pattern: /\[TOKEN:CANCELLATION_HELP\]/g,
      action: 'cancellation_help' as ActionType,
      type: 'primary' as const,
      label: 'Cancel Subscription',
    },
    {
      // Generate specific navigation button pattern: [BUTTON:generateSpecificNavigationButton:Label|path:PATH|icon:ICON]
      pattern: /\[BUTTON:generateSpecificNavigationButton:([^|\]]+)\|path:([^|\]]+)(?:\|icon:([^|\]]+))?\]/g,
      action: 'generate_specific_navigation_button' as ActionType,
      type: 'secondary' as const,
    },
  ];

  buttonPatterns.forEach(({ pattern, action, type, label: defaultLabel }) => {
    // eslint-disable-next-line no-cond-assign
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = pattern.exec(responseText)) !== null) {
      const buttonData: any = {
        originalText: match[0], // Full match
      };

      let label = defaultLabel || action.replace(/_/g, ' ');

      // Handle different button types with their specific parameters
      if (action === 'generate_specific_navigation_button') {
        // Navigation: [BUTTON:generateSpecificNavigationButton:DisplayText|path:PATH|icon:ICON]
        const [, displayText, navigationPath, icon] = match;
        label = displayText;
        buttonData.displayText = displayText;
        buttonData.navigationPath = navigationPath;
        buttonData.icon = icon;
      } else {
        // Other buttons: [BUTTON:ACTION:Label|fileId:FILE_ID] or [BUTTON:ACTION:Label]
        const [, buttonLabel, fileId] = match;
        label = buttonLabel || defaultLabel || action.replace(/_/g, ' ');

        // Extract file ID if present (for file-aware buttons)
        if (fileId) {
          buttonData.fileId = fileId;
        }
      }

      buttons.push({
        id: `${action}_${Date.now()}_${Math.random()}`,
        label,
        type,
        action,
        data: buttonData,
      });
    }
  });

  return buttons;
};

// Function to get file details including streams
export const getFileDetails = async (fileId: string): Promise<any> => {
  try {
    const response = await fetch(`${config.authBaseUrlV2}/files/${fileId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      console.error(
        'Failed to get file details:',
        response.status,
        response.statusText
      );
      return null;
    }

    const data = await response.json();
    return data.success ? data.data.file : null;
  } catch (error) {
    console.error('Error getting file details:', error);
    return null;
  }
};

// Function to handle button actions
export const handleButtonAction = async (
  button: ActionButton,
  context?: ChatContext
): Promise<{ success: boolean; message?: string; data?: any }> => {
  try {
    switch (button.action) {
      case 'reprocess_transcription': {
        // Check for file ID in button data first, then fall back to URL context
        const fileId = button.data?.fileId || context?.urlContext?.fileId;

        if (fileId) {
          // Get file details first to check streams
          const fileDetails = await getFileDetails(fileId);
          console.log('File details for reprocessing:', fileDetails);

          if (!fileDetails) {
            return {
              success: false,
              message: 'Could not retrieve file details. Please try again.',
            };
          }

          if (!fileDetails.streams?.includes('audio')) {
            return {
              success: false,
              message: `"${fileDetails.name}" does not contain audio and cannot be transcribed. Only videos or audio files with audio tracks can be transcribed.`,
            };
          }

          const success = await triggerReprocessTranscription(fileId);
          return {
            success,
            message: success
              ? `✅ Reprocessing started for "${fileDetails.name}"! \n\nThis typically takes 2-5 minutes depending on the video length. Please check back in a few minutes to see the improved transcription.`
              : 'Failed to start reprocessing. Please try again.',
            data: {
              fileId,
              fileName: fileDetails.name,
            },
          };
        }
        return {
          success: false,
          message:
            'No file selected for reprocessing. Please navigate to a file page first or provide a file URL.',
        };
      }

      case 'go_to_billing': {
        // Check button label to determine correct path
        const isUsageButton =
          button.label?.toLowerCase().includes('usage') ||
          button.label?.toLowerCase().includes('check usage');
        const redirectPath = isUsageButton
          ? '/settings/usage'
          : '/settings/billing';

        // Check if user is already on the target page
        const currentPath =
          context?.urlContext?.path ||
          window?.location?.hash?.replace('#', '') ||
          window?.location?.pathname;
        const isAlreadyOnTarget = currentPath?.includes(
          redirectPath.replace('/', '')
        );

        if (isAlreadyOnTarget) {
          const pageType = isUsageButton ? 'usage details' : 'billing settings';
          return {
            success: true,
            message: `You're already on the ${pageType} page! 📍\n\nYou can see your current usage information displayed above. ${
              isUsageButton
                ? 'Your usage limits and remaining quotas are shown in the dashboard.'
                : 'All billing information and subscription details are available here.'
            }`,
            data: {
              // Don't redirect if already on target page
              redirect: null,
            },
          };
        }

        const messageText = isUsageButton
          ? 'Opening usage details...'
          : 'Opening billing settings...';

        return {
          success: true,
          message: messageText,
          data: {
            redirect: redirectPath,
            openInNewTab: false,
          },
        };
      }

      case 'clear_cache_and_storage':
        try {
          // Clear localStorage
          localStorage.clear();

          // Clear sessionStorage
          sessionStorage.clear();

          // Clear indexedDB if available
          if ('indexedDB' in window) {
            indexedDB
              .databases()
              .then((databases) => {
                databases.forEach((db) => {
                  if (db.name) {
                    indexedDB.deleteDatabase(db.name);
                  }
                });
              })
              .catch(() => {
                // Silently handle if indexedDB.databases() is not available
              });
          }

          // Clear service worker cache if available
          if ('caches' in window) {
            caches
              .keys()
              .then((cacheNames) => {
                return Promise.all(
                  cacheNames.map((cacheName) => caches.delete(cacheName))
                );
              })
              .catch(() => {
                // Silently handle cache clearing errors
              });
          }

          return {
            success: true,
            message:
              '✅ Cache and local storage cleared successfully!\n\n⚠️ You will be logged out automatically in 3 seconds. Please log back in to continue using ScreenApp.',
            data: {
              logout: true,
              delay: 3000, // 3 seconds delay before logout
            },
          };
        } catch (error) {
          console.error('Error clearing cache and storage:', error);
          return {
            success: false,
            message:
              'Failed to clear cache and local storage. Please try refreshing the page manually.',
          };
        }

      case 'cancellation_help':
        return {
          success: true,
          message: 'Opening cancellation help...',
          data: {
            redirect: '/settings/billing',
            openInNewTab: false,
          },
        };

      case 'generate_specific_navigation_button': {
        const targetPath = button.data?.navigationPath;
        const displayText = button.data?.displayText || 'the requested section';

        // Check if user is already on the target page
        const currentPath =
          context?.urlContext?.path ||
          window?.location?.hash?.replace('#', '') ||
          window?.location?.pathname;
        const isAlreadyOnTarget =
          currentPath?.includes(targetPath?.replace('/', '')) ||
          currentPath === targetPath ||
          (targetPath && currentPath?.endsWith(targetPath.split('/').pop()));

        if (isAlreadyOnTarget) {
          return {
            success: true,
            message: `You're already on the ${displayText} page! 📍\n\nYou can see all the relevant information displayed above. The content you're looking for should be available on this current page.`,
            data: {
              // Don't redirect if already on target page
              redirect: null,
            },
          };
        }

        return {
          success: true,
          message: `Navigating to ${displayText}...`,
          data: {
            redirect: targetPath,
            openInNewTab: false,
            displayText,
            icon: button.data?.icon,
          },
        };
      }

      default:
        return {
          success: false,
          message: 'Unknown action type.',
        };
    }
  } catch (error) {
    console.error('Error handling button action:', error);
    return {
      success: false,
      message: 'An error occurred while processing the action.',
    };
  }
};

// Function to clean response text by removing button patterns and tokens
export const cleanResponseText = (responseText: string): string => {
  return responseText
    .replace(/\[BUTTON:[^|\]]+(?:\|[^|\]]+)*\]/g, '') // Handle both basic and file-aware buttons
    .replace(/\[TOKEN:[^\]]+\]/g, '') // Remove all TOKEN patterns including SUBSCRIPTION_PLANS
    .replace(/\n\s*\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to max 2
    .trim();
};

// Enhanced version that returns full response with tool calls and action buttons
export const sendChatMessageWithActions = async (
  message: string,
  context?: ChatContext,
  conversationHistory?: ChatMessage[],
  sessionId?: string
): Promise<{
  message: string;
  sessionId?: string;
  sessionTitle?: string;
  toolCalls?: ToolCall[];
  shouldEscalate?: boolean;
  escalated?: boolean;
  actionButtons?: ActionButton[];
}> => {
  try {
    console.log(
      'Making enhanced chat request to:',
      `${config.authBaseUrlV2}/chat`
    );
    console.log('Request payload:', {
      message,
      context,
      conversationHistory,
      sessionId,
    });

    const response = await fetch(`${config.authBaseUrlV2}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        message,
        context,
        conversationHistory,
        sessionId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.log('Error response:', errorText);
      throw new Error(
        `Failed to send chat message: ${response.status} ${response.statusText}`
      );
    }

    const data: ChatResponse = await response.json();
    console.log('Enhanced response data:', data);

    if (!data.success) {
      throw new Error(data.message || 'Failed to send chat message');
    }

    const responseMessage =
      data.data?.message || 'Sorry, I could not process your request.';
    const actionButtons = parseActionButtons(responseMessage);
    const cleanedMessage = cleanResponseText(responseMessage);

    return {
      message: cleanedMessage,
      sessionId: data.data?.sessionId,
      sessionTitle: data.data?.sessionTitle,
      toolCalls: data.data?.toolCalls,
      shouldEscalate: data.data?.shouldEscalate,
      escalated: data.data?.escalated,
      actionButtons,
    };
  } catch (error) {
    console.error('Error sending enhanced chat message:', error);
    throw new Error('Failed to send chat message');
  }
};

// Get chat history
export const getChatHistory = async (
  page: number = 1,
  limit: number = 10
): Promise<ChatHistoryResponse> => {
  try {
    const response = await fetch(
      `${config.authBaseUrlV2}/chat/history?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to get chat history: ${response.status} ${response.statusText}`
      );
    }

    const data: ChatHistoryResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting chat history:', error);
    throw new Error('Failed to get chat history');
  }
};

// Get specific chat session
export const getChatSession = async (
  sessionId: string
): Promise<ChatSessionResponse> => {
  try {
    const response = await fetch(
      `${config.authBaseUrlV2}/chat/history/${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to get chat session: ${response.status} ${response.statusText}`
      );
    }

    const data: ChatSessionResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting chat session:', error);
    throw new Error('Failed to get chat session');
  }
};

// Delete chat session
export const deleteChatSession = async (
  sessionId: string
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${config.authBaseUrlV2}/chat/history/${sessionId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to delete chat session: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Error deleting chat session:', error);
    throw new Error('Failed to delete chat session');
  }
};

export const sendChatMessageStreaming = async (
  message: string,
  context?: ChatContext,
  conversationHistory?: ChatMessage[],
  onToken?: (token: string) => void
): Promise<string> => {
  try {
    const response = await fetch(`${config.authBaseUrlV2}/chat?stream=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        message,
        context,
        conversationHistory,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to send chat message: ${response.status} ${response.statusText}`
      );
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        fullResponse += chunk;

        if (onToken) {
          onToken(chunk);
        }
      }
    } finally {
      reader.releaseLock();
    }

    return fullResponse;
  } catch (error) {
    console.error('Error sending streaming chat message:', error);
    throw new Error('Failed to send chat message');
  }
};

// Enhanced streaming chat with actions support
export const sendChatMessageWithActionsStreaming = async (
  message: string,
  context?: ChatContext,
  conversationHistory?: ChatMessage[],
  sessionId?: string,
  onToken?: (token: string) => void,
  multiAgentOptions?: {
    useMultiAgent?: boolean;
    forceAgent?: string;
  }
): Promise<{
  message: string;
  sessionId?: string;
  sessionTitle?: string;
  toolCalls?: ToolCall[];
  shouldEscalate?: boolean;
  escalated?: boolean;
  actionButtons?: ActionButton[];
  selectedAgent?: string;
  routingConfidence?: number;
}> => {
  try {
    // For now, we'll use the regular JSON endpoint but collect response in real-time
    // This provides better UX than waiting for the full response
    const response = await fetch(`${config.authBaseUrlV2}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        message,
        context,
        conversationHistory,
        sessionId,
        useMultiAgent: multiAgentOptions?.useMultiAgent ?? true,
        forceAgent: multiAgentOptions?.forceAgent,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.log('Error response:', errorText);
      throw new Error(
        `Failed to send chat message: ${response.status} ${response.statusText}`
      );
    }

    const data: ChatResponse = await response.json();
    console.log('Enhanced streaming response data:', data);

    if (!data.success) {
      throw new Error(data.message || 'Failed to send chat message');
    }

    const responseMessage =
      data.data?.message || 'Sorry, I could not process your request.';

    // Clean the response message first, then simulate streaming
    const cleanedForStreaming = cleanResponseText(responseMessage);

    // Simulate streaming by delivering the cleaned response word by word
    if (onToken) {
      const words = cleanedForStreaming.split(' ');
      for (let i = 0; i < words.length; i += 1) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '');
        onToken(word);
        // Faster streaming for better UX - reduced delay
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 8));
      }
    }

    const actionButtons = parseActionButtons(responseMessage);
    const cleanedMessage = cleanResponseText(responseMessage);

    return {
      message: cleanedMessage,
      sessionId: data.data?.sessionId,
      sessionTitle: data.data?.sessionTitle,
      toolCalls: data.data?.toolCalls,
      shouldEscalate: data.data?.shouldEscalate,
      escalated: data.data?.escalated,
      actionButtons,
      selectedAgent: data.data?.selectedAgent,
      routingConfidence: data.data?.routingConfidence,
    };
  } catch (error) {
    console.error('Error sending enhanced streaming chat message:', error);
    throw new Error('Failed to send chat message');
  }
};

// Function to send chat message feedback
export const sendChatFeedback = async (
  messageId: string,
  sessionId: string,
  feedbackType: 'like' | 'dislike',
  comment?: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await fetch(`${config.authBaseUrlV2}/chat/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        messageId,
        sessionId,
        feedbackType,
        comment,
        timestamp: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      console.error(
        'Failed to send feedback:',
        response.status,
        response.statusText
      );
      return {
        success: false,
        message: 'Failed to send feedback. Please try again.',
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      message: data.message,
    };
  } catch (error) {
    console.error('Error sending feedback:', error);
    return {
      success: false,
      message: 'Failed to send feedback. Please try again.',
    };
  }
};
